import {
  InteractionRequiredAuthError,
  BrowserAuthError,
  BrowserAuthErrorCodes,
} from "@azure/msal-browser";
import { msalInstance, apiScopes } from "@/auth/msalConfig";

/**
 * Returns true if the error indicates the user must re-authenticate interactively.
 * This includes explicit "interaction_required" errors from AAD as well as
 * BrowserAuthError timeouts (silent iframe failed — session likely expired).
 */
function requiresInteraction(error: unknown): boolean {
  if (error instanceof InteractionRequiredAuthError) {
    return true;
  }
  if (
    error instanceof BrowserAuthError &&
    error.errorCode === BrowserAuthErrorCodes.timedOut
  ) {
    return true;
  }
  return false;
}

export async function getAccessToken(): Promise<string> {
  const account = msalInstance.getActiveAccount();
  if (!account) {
    throw new Error("No active account. User must be logged in.");
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...apiScopes,
      account,
    });
    return response.accessToken;
  } catch (error) {
    if (requiresInteraction(error)) {
      // Token expired, consent needed, or silent iframe timed out — trigger interactive login
      await msalInstance.acquireTokenRedirect(apiScopes);
      // Won't reach here — page redirects
      throw error;
    }
    throw error;
  }
}

/**
 * Like getAccessToken, but for routes that must work whether or not the caller is signed in
 * (e.g. the public booking flow). Never triggers an interactive/redirect login — a stale or
 * expired MSAL session just resolves to null so the request proceeds unauthenticated instead of
 * yanking the user away from whatever they were doing.
 */
export async function getAccessTokenSilentOrNull(): Promise<string | null> {
  const account = msalInstance.getActiveAccount();
  if (!account) {
    return null;
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...apiScopes,
      account,
    });
    return response.accessToken;
  } catch {
    return null;
  }
}
